import { BorderRadiusSmall } from 'autogen/design-tokens/tokens';
import type { ResponsiveImageModel } from 'autogen/swagger/all';
import type { ConnectedContentInlineModel } from 'autogen/swagger/all';
import PreviewBackgroundImage from 'components/connectedContent/inlineConnectedContent/shared/previewBackgroundImage';
import {
    Button,
    Container,
    Heading,
    PageActionWrapper,
    Text,
    TextContainer
} from 'components/connectedContent/inlineConnectedContent/shared/styles';
import useAdformImpressionTracking from 'hooks/tracking/useAdformImpressionTracking';
import { debounce } from 'lodash-es';
import matchMedia from 'modules/helpers/matchMedia';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import Author from './author';
import { breakpoint, sizes } from 'variables';
import PictureDeprecated from 'components/generic/picture/PictureDeprecated';

const StyledPageActionWrapper = styled(PageActionWrapper)`
    flex-direction: column;

    &:hover,
    &:focus {
        text-decoration: none;
    }
`;

const StyledContainer = styled(Container)`
    width: 100%;
    border-top: 0;
    border-radius: 0 0 ${BorderRadiusSmall} ${BorderRadiusSmall};
`;

const StyledTextContainer = styled(TextContainer)`
    border-top: 0;
    margin-top: auto;
    margin-bottom: 0;
`;

const ButtonWrapper = styled.div`
    display: flex;
    width: 100%;
    justify-content: center;
`;

const ImageContainer = styled.div`
    display: flex;
    justify-content: center;
    flex: 1 1 0;
    position: relative;
    height: 100%;
    border-radius: ${BorderRadiusSmall} ${BorderRadiusSmall} 0 0;
    overflow: hidden;
`;

const StyledPicture = styled(PictureDeprecated)`
    width: 100%;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

interface IVerticalLayout {
    model: ConnectedContentInlineModel;
    onClick: () => void;
}

export default function VerticalLayout({ model, onClick }: IVerticalLayout) {
    const {
        image,
        link,
        widthType,
        dataLayer,
        ctaText,
        isPreview,
        designTemplate,
        heading,
        headingMobile,
        text,
        textMobile,
        advisorModel,
        adformTracking
    } = model;

    const { backgroundColor, headerColor, textColor, textAlignmentType, ctaBorderColor, ctaButtonColor, ctaTextColor } =
        designTemplate;

    const { pageAction } = link || {};

    const hasTextContainer = heading || text || (ctaText && pageAction);

    const [isDesktop, setIsDesktop] = useState(matchMedia.isDesktop);
    const handleResize = debounce(() => setIsDesktop(matchMedia.isDesktop), 100);
    const imageRef = useRef<HTMLImageElement | null>(null);

    useAdformImpressionTracking({
        ref: imageRef,
        impTrackingUrls: adformTracking?.impTrackers
    });

    function setSizes(imageObject: ResponsiveImageModel, sizesString: string) {
        return { ...imageObject, sources: imageObject.sources.map((x) => ({ ...x, sizes: sizesString })) };
    }

    useEffect(() => {
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [handleResize]);

    if (!link?.pageAction) return null;

    return (
        <StyledPageActionWrapper
            action={link.pageAction}
            onClick={onClick}
            isPreview={isPreview}
            $widthType={widthType}
            data-datalayer={JSON.stringify(dataLayer)}
        >
            <ImageContainer>
                {isPreview ? (
                    <PreviewBackgroundImage {...image} />
                ) : (
                    <StyledPicture ref={imageRef} {...setSizes(image, `${breakpoint.up(sizes.md)} 220px, 50vw`)} />
                )}
                <Author advisorModel={advisorModel} />
            </ImageContainer>
            {hasTextContainer && (
                <StyledContainer backgroundColor={backgroundColor}>
                    <StyledTextContainer textAlignmentType={textAlignmentType}>
                        {heading && (
                            <Heading color={headerColor}>
                                {!isDesktop && headingMobile ? headingMobile : heading}
                            </Heading>
                        )}
                        {text && <Text color={textColor}>{!isDesktop && textMobile ? textMobile : text}</Text>}
                    </StyledTextContainer>
                    {ctaText && (
                        <ButtonWrapper>
                            <Button
                                ctaButtonColor={ctaButtonColor}
                                ctaTextColor={ctaTextColor}
                                ctaBorderColor={ctaBorderColor}
                            >
                                {ctaText}
                            </Button>
                        </ButtonWrapper>
                    )}
                </StyledContainer>
            )}
        </StyledPageActionWrapper>
    );
}
